import { cn } from "@zero/utils";
import { Pencil, Tags, Trash2, X } from "lucide-react";
import { useState } from "react";
import { useTodoStore } from "../store/todoStore";

type Props = {
  open: boolean;
  onClose: () => void;
};

export function CategoryManager({ open, onClose }: Props) {
  const { categories, createCategory, renameCategory, deleteCategory, isLoading } = useTodoStore();
  const categoryNames = categories();
  const [newCategory, setNewCategory] = useState("");
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [nextName, setNextName] = useState("");

  if (!open) {
    return null;
  }

  const startEdit = (category: string) => {
    setEditingCategory(category);
    setNextName(category);
  };

  const saveEdit = async () => {
    if (!editingCategory || !nextName.trim() || isLoading) return;
    try {
      await renameCategory(editingCategory, nextName);
      setEditingCategory(null);
      setNextName("");
    } catch (_error) {
      // Store/API layer exposes the user-facing error state.
    }
  };

  const addCategory = () => {
    if (!newCategory.trim() || isLoading) return;
    createCategory(newCategory);
    setNewCategory("");
  };

  const removeCategory = async (category: string) => {
    if (isLoading) return;
    try {
      await deleteCategory(category);
    } catch (_error) {
      // Store/API layer exposes the user-facing error state.
    }
  };

  return (
    <div className="mb-4 rounded-md border border-border bg-card p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Tags size={16} />
          分类管理
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="关闭分类管理"
        >
          <X size={16} />
        </button>
      </div>

      <div className="mb-3 flex gap-2">
        <input
          type="text"
          value={newCategory}
          onChange={(event) => setNewCategory(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              addCategory();
            }
          }}
          className="h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring"
          placeholder="新增分类"
          aria-label="新增分类"
        />
        <button
          type="button"
          onClick={addCategory}
          disabled={!newCategory.trim() || isLoading}
          className="h-9 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
        >
          新增
        </button>
      </div>

      <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
        {categoryNames.map((category) => (
          <div key={category} className="flex items-center gap-2 rounded-md border border-border bg-background p-2">
            {editingCategory === category ? (
              <>
                <input
                  type="text"
                  value={nextName}
                  onChange={(event) => setNextName(event.target.value)}
                  className="h-8 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring"
                  aria-label="分类名称"
                />
                <button
                  type="button"
                  onClick={saveEdit}
                  disabled={!nextName.trim() || isLoading}
                  className="h-8 rounded-md bg-primary px-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                >
                  保存
                </button>
              </>
            ) : (
              <>
                <span className="min-w-0 flex-1 truncate text-sm text-foreground">{category}</span>
                <button
                  type="button"
                  onClick={() => startEdit(category)}
                  disabled={isLoading}
                  className={cn(
                    "inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground",
                    isLoading && "cursor-not-allowed opacity-50",
                  )}
                  aria-label={`重命名 ${category}`}
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => removeCategory(category)}
                  disabled={isLoading || category === "其他"}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label={`删除 ${category}`}
                >
                  <Trash2 size={14} />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
